// src/pages/Register.jsx
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import registerVideo from '../assets/register.mp4';

function Register() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [passwordConfirmation, setPasswordConfirmation] = useState('');
  const [message, setMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [passwordError, setPasswordError] = useState('');
  const [confirmPasswordError, setConfirmPasswordError] = useState('');
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setIsVisible(true), 100);
    return () => clearTimeout(timer);
  }, []);

  const handlePasswordChange = (e) => {
    const value = e.target.value;
    setPassword(value);
    setPasswordError(value.length < 8 ? t('auth.register.errorPasswordShort') : '');
    if (passwordConfirmation && value !== passwordConfirmation) {
      setConfirmPasswordError(t('auth.register.errorPasswordMismatch'));
    } else {
      setConfirmPasswordError('');
    }
  };

  const handleConfirmPasswordChange = (e) => {
    setPasswordConfirmation(e.target.value);
    setConfirmPasswordError(
      e.target.value !== password ? t('auth.register.errorPasswordMismatch') : ''
    );
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    if (password.length < 8) {
      setPasswordError(t('auth.register.errorPasswordShort'));
      return;
    }
    if (password !== passwordConfirmation) {
      setConfirmPasswordError(t('auth.register.errorPasswordMismatch'));
      return;
    }

    setIsLoading(true);
    setMessage('');
    try {
      await axios.post('https://madadline.com/api/register', {
        name,
        email,
        password,
        password_confirmation: passwordConfirmation,
      });
      navigate('/login');
    } catch (error) {
      setMessage(error.response?.data?.message || t('auth.register.errorGeneric'));
      setIsLoading(false);
    }
  };

  return (
    <div className="auth-creative">

      <div className="auth-creative__deco auth-creative__deco--1" />
      <div className="auth-creative__deco auth-creative__deco--2" />
      <div className="auth-creative__deco auth-creative__deco--3" />

      <div className="auth-creative__visual">
        <video className="auth-creative__video" autoPlay loop muted playsInline>
          <source src={registerVideo} type="video/mp4" />
        </video>
        <div className="auth-creative__visual-overlay" />

        <div className="auth-creative__visual-content">
          <span className="auth-creative__eyebrow">{t('auth.visualEyebrow')}</span>

          <h1 className="auth-creative__visual-title">
            {t('auth.register.visualTitleLine1')}<br />
            <em>{t('auth.register.visualTitleEm')}</em>
          </h1>

          <p className="auth-creative__visual-text">
            {t('auth.register.visualText')}
          </p>

          <div className="auth-creative__stats">
            <div className="auth-creative__stat">
              <span className="auth-creative__stat-value">5000+</span>
              <span className="auth-creative__stat-label">{t('auth.statsClients')}</span>
            </div>
            <div className="auth-creative__stat">
              <span className="auth-creative__stat-value">150+</span>
              <span className="auth-creative__stat-label">{t('auth.statsCities')}</span>
            </div>
            <div className="auth-creative__stat">
              <span className="auth-creative__stat-value">24/7</span>
              <span className="auth-creative__stat-label">{t('auth.statsSupport')}</span>
            </div>
          </div>

          <div className="auth-creative__quote">
            <span className="auth-creative__quote-mark">"</span>
            <p>{t('auth.register.quote')}</p>
          </div>
        </div>

        <div className="auth-creative__badge">
          <span className="auth-creative__badge-ar">مداد</span>
          <span className="auth-creative__badge-sep" />
          <span className="auth-creative__badge-fr">MADAD</span>
        </div>
      </div>

      <div className="auth-creative__form-side">
        <div className="auth-creative__form-container">

          <Link to="/" className="auth-creative__logo">
            <div className="auth-creative__logo-mark">
              <span className="auth-creative__logo-ar">مداد</span>
              <span className="auth-creative__logo-fr">MADAD</span>
            </div>
            <span className="auth-creative__logo-tag">{t('auth.logoTag')}</span>
          </Link>

          <div
            className="auth-creative__header"
            style={{
              opacity: isVisible ? 1 : 0,
              transform: isVisible ? 'translateY(0)' : 'translateY(20px)',
              transition: 'all 0.6s ease',
            }}
          >
            <h2>{t('auth.register.heading')}</h2>
            <p>{t('auth.register.subheading')}</p>
          </div>

          <form className="auth-creative__form" onSubmit={handleRegister}>
            <div
              className="auth-creative__field"
              style={{
                opacity: isVisible ? 1 : 0,
                transform: isVisible ? 'translateX(0)' : 'translateX(20px)',
                transition: 'all 0.5s ease 0.1s',
              }}
            >
              <label htmlFor="name">{t('auth.register.labelName')}</label>
              <input
                id="name"
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder={t('auth.register.placeholderName')}
                autoComplete="name"
                required
              />
              <span className="auth-creative__field-line" />
            </div>

            <div
              className="auth-creative__field"
              style={{
                opacity: isVisible ? 1 : 0,
                transform: isVisible ? 'translateX(0)' : 'translateX(20px)',
                transition: 'all 0.5s ease 0.2s',
              }}
            >
              <label htmlFor="email">{t('auth.register.labelEmail')}</label>
              <input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder={t('auth.register.placeholderEmail')}
                autoComplete="email"
                required
              />
              <span className="auth-creative__field-line" />
            </div>

            <div
              className="auth-creative__field"
              style={{
                opacity: isVisible ? 1 : 0,
                transform: isVisible ? 'translateX(0)' : 'translateX(20px)',
                transition: 'all 0.5s ease 0.3s',
              }}
            >
              <label htmlFor="password">{t('auth.register.labelPassword')}</label>
              <input
                id="password"
                type="password"
                value={password}
                onChange={handlePasswordChange}
                placeholder={t('auth.register.placeholderPassword')}
                autoComplete="new-password"
                required
              />
              <span className="auth-creative__field-line" />
              {passwordError && (
                <span className="auth-creative__error">{passwordError}</span>
              )}
            </div>

            <div
              className="auth-creative__field"
              style={{
                opacity: isVisible ? 1 : 0,
                transform: isVisible ? 'translateX(0)' : 'translateX(20px)',
                transition: 'all 0.5s ease 0.4s',
              }}
            >
              <label htmlFor="confirm">{t('auth.register.labelConfirm')}</label>
              <input
                id="confirm"
                type="password"
                value={passwordConfirmation}
                onChange={handleConfirmPasswordChange}
                placeholder={t('auth.register.placeholderConfirm')}
                autoComplete="new-password"
                required
              />
              <span className="auth-creative__field-line" />
              {confirmPasswordError && (
                <span className="auth-creative__error">{confirmPasswordError}</span>
              )}
            </div>

            <button
              type="submit"
              className="auth-creative__submit"
              disabled={isLoading}
              style={{
                opacity: isVisible ? 1 : 0,
                transform: isVisible ? 'translateY(0)' : 'translateY(10px)',
                transition: 'all 0.5s ease 0.5s',
              }}
            >
              {isLoading ? (
                <span className="auth-creative__spinner" />
              ) : (
                <>
                  {t('auth.register.submit')}
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <line x1="5" y1="12" x2="19" y2="12" />
                    <polyline points="12 5 19 12 12 19" />
                  </svg>
                </>
              )}
            </button>

            {message && <div className="auth-creative__message">{message}</div>}
          </form>

          <div className="auth-creative__divider">
            <span /><em>{t('auth.divider')}</em><span />
          </div>

          <p className="auth-creative__switch">
            {t('auth.register.switchText')}{' '}
            <Link to="/login">{t('auth.register.switchLink')}</Link>
          </p>

        </div>
      </div>
    </div>
  );
}

export default Register;